import type { TelemetryBrowserInstance } from 'telemetry/browser'
import { logger } from 'utils'

let telemetryInstance: TelemetryBrowserInstance | null = null
let isInitialized = false

type TelemetryContext = {
    organizationSlug?: string
    agentId?: string
    userId?: string
}

let telemetryContext: TelemetryContext = {}

function mergedContext(
    properties?: Record<string, string>,
): Record<string, string> {
    const base: Record<string, string> = {
        source: 'web-component',
        hostOrigin: window.location.origin,
        hostUrl: window.location.href,
    }

    if (telemetryContext.organizationSlug) {
        base.organizationSlug = telemetryContext.organizationSlug
    }
    if (telemetryContext.agentId) {
        base.agentId = telemetryContext.agentId
    }
    if (telemetryContext.userId) {
        base.userId = telemetryContext.userId
    }

    return {
        ...base,
        ...(properties ?? {}),
    }
}

/**
 * Initializes OpenTelemetry for the web component widget, as the backend of
 * its installation asks in the agent access: one bundle runs on every
 * installation, so nothing of this is decided at build time. The OTLP
 * exporter targets that backend's `/api/otel` proxy.
 *
 * The SDK is loaded through a dynamic `import()`, so it never blocks the
 * first render: the browser build of OpenTelemetry weighs about 37 kB
 * gzipped, and the widget is served from a CDN to pages that are not ours.
 * Nothing waits for it. What it collects is automatic instrumentation
 * (document load, fetch), so a call made before the chunk lands is simply
 * not traced; `trackAppEvent` and the rest write to the logger and do not
 * read the instance at all.
 *
 * Uses a singleton pattern: only the first call takes effect. An
 * installation that collects no telemetry never calls it, which is what keeps
 * the chunk from being fetched at all.
 */
export function initializeTelemetry(config: {
    backendUrl: string
    environment: string
    samplingRate: number
}): void {
    if (isInitialized) {
        return
    }
    isInitialized = true

    void import('telemetry/browser')
        .then(({ initializeTelemetryBrowser }) => {
            telemetryInstance = initializeTelemetryBrowser({
                serviceName: 'web-component',
                environment: config.environment,
                exporterEndpoint: `${config.backendUrl}/api/otel`,
                samplingRate: Math.max(0, Math.min(1, config.samplingRate)),
            })

            window.addEventListener('beforeunload', async () => {
                await telemetryInstance?.flush()
                await telemetryInstance?.shutdown()
            })

            logger.info(
                '[telemetry] web-component initialized',
                mergedContext(),
            )
        })
        .catch((caught) => {
            // The host page keeps working without telemetry, so this is worth
            // a line in the console and nothing more.
            logger.warn(
                '[telemetry] the browser SDK could not be loaded',
                caught,
            )
        })
}

/**
 * Updates telemetry context enriched onto every subsequent event.
 */
export function setTelemetryContext(ctx: Partial<TelemetryContext>): void {
    telemetryContext = { ...telemetryContext, ...ctx }
}

/**
 * Tracks a custom event when telemetry is initialized.
 */
export function trackAppEvent(
    name: string,
    properties?: Record<string, string>,
): void {
    logger.debug('[telemetry:event]', name, mergedContext(properties))
}

/**
 * Tracks a custom metric when telemetry is initialized.
 */
export function trackAppMetric(
    name: string,
    average: number,
    properties?: Record<string, string>,
): void {
    logger.debug('[telemetry:metric]', name, average, mergedContext(properties))
}

/**
 * Tracks an exception when telemetry is initialized.
 */
export function trackAppException(
    error: Error,
    properties?: Record<string, string>,
): void {
    logger.error(error, mergedContext(properties))
}
